import { useMemo, useState } from "react";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { Trans, useTranslation } from "react-i18next";
import { Alert } from "@/components/ui/alert";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import type { Filterdescriptor } from "@/components/ui/data-table-filter";
import { ListDetail } from "@/components/ListDetail";
import { PageSkeleton } from "@/components/PageSkeleton";
import { Shell } from "@/components/Shell";
import { SignIn } from "@/features/session/SignIn";
import { BeschikbaarheidForm } from "@/features/beschikbaarheid/BeschikbaarheidForm";
import { useOndersteunerPeriodes } from "@/features/beschikbaarheid/useBeschikbaarheid";
import { InschrijfperiodeKiezer } from "@/features/inschrijfperiodes/InschrijfperiodeKiezer";
import type { Inschrijfperiode } from "@/features/inschrijfperiodes/useInschrijfperiodes";
import {
  huidigePeriode,
  vensterStatus,
} from "@/features/inschrijfperiodes/venster";
import { PersoonZoeker } from "@/features/koppelingen/PersoonZoeker";
import { VbsSyncWaarschuwing } from "@/features/ondersteuners/VbsSyncWaarschuwing";
import { useZoekPersonen } from "@/features/koppelingen/useKoppelingen";
import { UNPAGINATED_FETCH_SIZE } from "@/lib/paging";
import { useFilterStand } from "@/lib/useFilterStand";
import { useMe } from "@/features/session/useMe";
import { kiesSeizoen, useSeizoenen } from "@/features/session/useSeizoenen";
import { keepScrollPosition } from "@jbnl/shared-ui";

/**
 * Namens handelen on the persoon axis. The same endpoint as the Aanmeldsite's card, so the two
 * produce identical rows and the trail says who acted and for whom (ADR-0022). Named for the
 * party, not the payload: beschikbaarheid is what the form writes, the screen is the persoon.
 */
export function OndersteunersPage() {
  const { t } = useTranslation(["paginas", "navigatie", "common"]);
  const { data: me, isPending } = useMe();
  const { data: seizoenen } = useSeizoenen();
  // This screen carries no `seizoen` search param of its own; the one last picked on the
  // Seizoenen screen wins over the running one, same as Ploegen and the Overzicht. Reading
  // `seizoenen[0]` here left it on the running seizoen while every screen around it had
  // followed the pick - and with it the periodes, so a beheerder standing on 2026 was handed
  // 2027's inschrijfperiode to correct.
  const seizoen = kiesSeizoen(seizoenen);
  const { data: periodes } = useOndersteunerPeriodes(seizoen?.id);
  const { persoon: persoonId, q } = useSearch({ from: "/ondersteuners" });
  const navigate = useNavigate({ from: "/ondersteuners" });
  // Held above the panes: a beheerder correcting several ondersteuners works through one window,
  // so the pick has to survive choosing the next persoon.
  const [gekozenPeriodeId, setGekozenPeriodeId] = useState<string>();

  // Mirrors the ploegen screen's own filter, on the persoon axis. Server-driven, so the
  // descriptor carries no `past` and the facet shows no count (#192).
  const descriptors = useMemo<Filterdescriptor<unknown>[]>(
    () => [
      {
        id: "reedsIngeschreven",
        label: t("ondersteuners.filterReedsIngeschreven"),
        kind: "keuze",
        opties: [
          {
            waarde: "verbergen",
            label: t("ondersteuners.filterReedsIngeschrevenVerbergen"),
          },
          {
            waarde: "alleen",
            label: t("ondersteuners.filterReedsIngeschrevenAlleen"),
          },
        ],
      },
    ],
    [t],
  );
  const { stand, onStand } = useFilterStand(descriptors, "/ondersteuners");

  if (isPending) {
    return (
      <Shell>
        <PageSkeleton />
      </Shell>
    );
  }

  if (!me) {
    return (
      <Shell>
        <SignIn />
      </Shell>
    );
  }

  // The API refuses this to anyone else regardless - a regiocontact's grant names korpsen and
  // these rows have none. Hiding it here only avoids showing a screen that would answer 403.
  if (!me.isBeheerder) {
    return (
      <Shell name={me.name} letterhead>
        <Card>
          <CardHeader>
            <CardTitle>{t("common:doors.noAccess")}</CardTitle>
            <CardDescription>
              {t("common:doors.noAccessDescription")}
            </CardDescription>
          </CardHeader>
        </Card>
      </Shell>
    );
  }

  // The open window to start with, since that is what a beheerder is usually here for (#175).
  const periode = bepaalPeriode(periodes, gekozenPeriodeId);
  const kies = (id: string | null) =>
    void navigate({
      ...keepScrollPosition,
      search: (huidig) => ({ ...huidig, persoon: id ?? undefined }),
    });

  // Replace, not push: a search is typed one letter at a time, and every letter would be a
  // separate stop on the way back to whatever the beheerder was doing before this screen.
  const zoek = (term: string) =>
    void navigate({
      ...keepScrollPosition,
      search: { q: term || undefined, persoon: undefined },
      replace: true,
    });

  return (
    <Shell name={me.name} wide>
      {/* The title above both panes rather than inside one, as on seizoenen and
          wedstrijddagen: a card wrapped around the pair cannot contain a list that has to
          scroll inside its own frame. */}
      <header className="mb-6 space-y-1">
        <h1 className="text-2xl font-bold tracking-[-0.02em]">
          {t("navigatie:paginas.ondersteuners.label")}
        </h1>
        <p className="text-sm text-muted-foreground">
          <SeizoenLabel seizoen={seizoen} />
        </p>
      </header>

      {/* Above the panes, not inside one: it is about the set of ondersteuners rather than about
          whichever one is selected, and a beheerder should meet it before they start searching
          for somebody who was never imported. */}
      <VbsSyncWaarschuwing />

      <ListDetail
        selected={Boolean(persoonId)}
        backLabel={t("ondersteuners.terug")}
        onBack={() => kies(null)}
        list={
          <Card>
            {/* No CardHeader: the page title above both panes already names the screen, and the
                zoeker carries the only label this pane needs. */}
            <CardContent className="pt-6">
              <PersoonZoeker
                term={q ?? ""}
                onZoek={zoek}
                gekozen={persoonId ?? null}
                onKies={kies}
                toonIedereen
                alleenOndersteuners
                exportable
                inschrijfperiodeId={periode?.id}
                filters={{ descriptors, stand, onStand }}
              />
            </CardContent>
          </Card>
        }
        detail={
          <DetailPane
            persoonId={persoonId}
            term={q ?? ""}
            periodes={periodes}
            periode={periode}
            onKiesPeriode={setGekozenPeriodeId}
          />
        }
      />
    </Shell>
  );
}

/** The window to start on: the one a beheerder just picked, else whichever huidigePeriode names (#175). */
function bepaalPeriode(
  periodes: Inschrijfperiode[] | undefined,
  gekozenPeriodeId: string | undefined,
) {
  return (
    periodes?.find((kandidaat) => kandidaat.id === gekozenPeriodeId) ??
    huidigePeriode(periodes)
  );
}

/** Own component for the seizoen-or-"geen" ternary, so the header's JSX stays a single line to scan. */
function SeizoenLabel({ seizoen }: { seizoen: { name: string } | undefined }) {
  const { t } = useTranslation("common");
  return seizoen
    ? t("seizoen.naam", { naam: seizoen.name })
    : t("seizoen.geen");
}

/** Keeps the persoonId ternary out of the page function: no persoon chosen shows the placeholder card instead. */
function DetailPane({
  persoonId,
  term,
  periodes,
  periode,
  onKiesPeriode,
}: {
  persoonId: string | undefined;
  term: string;
  periodes: Inschrijfperiode[] | undefined;
  periode: Inschrijfperiode | undefined;
  onKiesPeriode: (inschrijfperiodeId: string) => void;
}) {
  const { t } = useTranslation("paginas");

  if (!persoonId) {
    return (
      <Card>
        <CardContent className="pt-6">
          <p className="text-sm text-muted-foreground">
            {t("ondersteuners.kies")}
          </p>
        </CardContent>
      </Card>
    );
  }

  const venster = periode ? vensterStatus(periode) : undefined;

  return (
    <OndersteunerDetail
      persoonId={persoonId}
      term={term}
      periodes={periodes}
      inschrijfperiodeId={periode?.id}
      onKiesPeriode={onKiesPeriode}
      vensterGesloten={Boolean(venster && !venster.open)}
    />
  );
}

/**
 * Looks the naam up with its own search: the zoeker beside it is paged and may be on a page that
 * does not hold this persoon. A persoon past even that ceiling falls back to naming the thing
 * being edited, so the pane is never titleless.
 */
function OndersteunerDetail({
  persoonId,
  term,
  periodes,
  inschrijfperiodeId,
  onKiesPeriode,
  vensterGesloten,
}: {
  persoonId: string;
  term: string;
  periodes: Inschrijfperiode[] | undefined;
  inschrijfperiodeId: string | undefined;
  onKiesPeriode: (inschrijfperiodeId: string) => void;
  vensterGesloten: boolean;
}) {
  const { t } = useTranslation("paginas");
  // A lookup, not the picker: this reads the naam for a title rather than letting somebody
  // choose, so it fetches one generous page (the endpoint's own ceiling) rather than mirroring
  // whatever page the zoeker beside it happens to be on. A persoon past that ceiling still
  // opens correctly - `persoon` just falls back to the generic title below.
  const { data: gevonden } = useZoekPersonen(
    term,
    true,
    1,
    UNPAGINATED_FETCH_SIZE,
  );
  const persoon = gevonden?.items.find(
    (kandidaat) => kandidaat.persoonId === persoonId,
  );

  return (
    <Card>
      <CardHeader>
        <CardTitle>{persoon?.naam ?? t("ondersteuners.detailTitel")}</CardTitle>
        {/* Truncated, with the whole of it on hover: a masked address is one long unbroken
            token, and left to itself it runs straight out of the card. */}
        {persoon?.email && (
          <CardDescription className="truncate" title={persoon.email}>
            {persoon.email}
          </CardDescription>
        )}
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Namens handelen stays visible while it happens: the row records both people. */}
        <Alert tone="info">
          <Trans
            t={t}
            i18nKey="ondersteuners.namens"
            components={{ strong: <strong /> }}
          />
          {vensterGesloten && (
            <Trans
              t={t}
              i18nKey="ondersteuners.namensGesloten"
              components={{ em: <em /> }}
            />
          )}
        </Alert>

        {/* Under the namens alert, as on Ploegen: the chosen periode decides which
          wedstrijddagen the form below it draws. */}
        {periodes && periodes.length > 0 ? (
          <InschrijfperiodeKiezer
            id="ondersteuner-periode"
            className="max-w-sm"
            periodes={periodes}
            gekozen={inschrijfperiodeId}
            onKies={onKiesPeriode}
          />
        ) : (
          <p className="text-sm text-muted-foreground">
            {t("ondersteuners.geenPeriode")}
          </p>
        )}

        <BeschikbaarheidForm
          inschrijfperiodeId={inschrijfperiodeId}
          persoonId={persoonId}
        />
      </CardContent>
    </Card>
  );
}
